import { useFormik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { FiCalendar } from "react-icons/fi";
import { paymentValidationSchema } from "./validation/payment-validation";

function PaymentFormComponent() {
  const { i18n, t } = useTranslation();

  type InitialValuesType = {
    nameOnCard: string;
    cardNumber: string;
    CVV: string;
    expirationDate: string;
  };

  const initialValues: InitialValuesType = {
    nameOnCard: "",
    cardNumber: "",
    CVV: "",
    expirationDate: "",
  };

  const removeSymbolsFromInput = (
    evt: React.KeyboardEvent<HTMLInputElement>
  ) => {
    ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault();
  };

  const { values, errors, touched, handleChange, handleSubmit, handleBlur } =
    useFormik({
      initialValues: initialValues,
      validateOnChange: true,
      validateOnBlur: true,
      validationSchema: paymentValidationSchema,
      onSubmit: async () => {},
    });
  return (
    <div className="flex flex-col my-20 mobile:my-6">
      <form
        dir={i18n.language === "ar" ? "rtl" : "ltr"}
        onSubmit={handleSubmit}
        className="inline-flex flex-col gap-4 mobile:gap-2.5 items-center justify-center"
      >
        <div className="flex flex-col gap-2">
          <label className="text-zinc700 text-xl leading-[30px] font-arMedium">
            {t("paymentForm.component.nameOnCard")}
          </label>
          <input
            value={values.nameOnCard}
            onChange={handleChange}
            onBlur={handleBlur}
            id="nameOnCard"
            type="text"
            className={`${
              touched.nameOnCard && errors.nameOnCard
                ? "border-secondary-50 focus:border-secondary-50"
                : "border-zinc400 focus:border-mint-500"
            } w-[621px] h-[61px] bg-transparent mobile:max-w-[calc(100vw-24px)] mobile:h-[61px] px-4 py-[17px] rounded-[18px] border placeholder:text-zinc300 placeholder:text-lg placeholder:font-arMedium focus:outline-none caret-mint-500`}
            placeholder={`${t("placeholder.paymentForm.nameOnCard")}`}
          ></input>
          {touched.nameOnCard && errors.nameOnCard && (
            <p className="text-base mobile:text-xs font-arMedium text-secondary-50">
              {errors.nameOnCard}
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-zinc700 text-xl leading-[30px] font-arMedium">
            {t("paymentForm.component.cardNumber")}
          </label>
          <input
            onKeyDown={removeSymbolsFromInput}
            value={values.cardNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            id="cardNumber"
            type="number"
            className={`${
              touched.cardNumber && errors.cardNumber
                ? "border-secondary-50 focus:border-secondary-50"
                : "border-zinc400 focus:border-mint-500"
            } w-[621px] h-[61px] bg-transparent mobile:max-w-[calc(100vw-24px)] mobile:h-[61px] mobile px-4 py-[17px] rounded-[18px] border placeholder:text-zinc300 placeholder:text-lg placeholder:font-arMedium focus:outline-none caret-mint-500`}
            placeholder={`${t("placeholder.paymentForm.cardNumber")}`}
          ></input>
          {touched.cardNumber && errors.cardNumber && (
            <p className="text-base mobile:text-xs font-arMedium text-secondary-50">
              {errors.cardNumber}
            </p>
          )}
        </div>

        <div className="flex flex-row justify-between w-[621px] mobile:flex-col mobile:max-w-[calc(100vw-24px)] mobile:gap-2.5">
          <div className="flex flex-col gap-2">
            <label className="text-zinc700 text-xl leading-[30px] font-arMedium">
              {t("paymentForm.component.CVV")}
            </label>
            <input
              onKeyDown={removeSymbolsFromInput}
              value={values.CVV}
              onChange={handleChange}
              onBlur={handleBlur}
              id="CVV"
              type="number"
              className={`${
                touched.CVV && errors.CVV
                  ? "border-secondary-50 focus:border-secondary-50"
                  : "border-zinc400 focus:border-mint-500"
              } w-[306px] h-[61px] bg-transparent mobile:w-full mobile:h-[61px] px-4 py-[17px] rounded-[18px] border placeholder:text-zinc300 placeholder:text-lg placeholder:font-arMedium focus:outline-none caret-mint-500`}
              placeholder={`${t("placeholder.paymentForm.CVV")}`}
            ></input>
            {touched.CVV && errors.CVV && (
              <p className="w-auto text-base mobile:text-xs font-arMedium text-secondary-50">
                {errors.CVV}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-zinc700 text-xl leading-[30px] font-arMedium">
              {t("paymentForm.component.expirationDate")}
            </label>
            <div className="relative h-[61px]">
              {" "}
              <input
                onKeyDown={removeSymbolsFromInput}
                value={values.expirationDate}
                onChange={handleChange}
                onBlur={handleBlur}
                id="expirationDate"
                type="text"
                className={`${
                  touched.expirationDate && errors.expirationDate
                    ? "border-secondary-50 focus:border-secondary-50"
                    : "border-zinc400 focus:border-mint-500"
                } w-[306px] h-[61px] bg-transparent mobile:w-full mobile:h-[61px] px-4 py-[17px] rounded-[18px] border placeholder:text-zinc300 placeholder:text-lg placeholder:font-arMedium focus:outline-none caret-mint-500`}
                placeholder={`${t("placeholder.paymentForm.expirationDate")}`}
              ></input>{" "}
              <FiCalendar
                className={`${
                  i18n.language === "ar" ? "left-4" : "right-4"
                } absolute bottom-5 text-zinc-300 text-2xl`}
              />
            </div>

            {touched.expirationDate && errors.expirationDate && (
              <p className="w-auto text-base mobile:text-xs font-arMedium text-secondary-50">
                {errors.expirationDate}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-row gap-3.5 place-self-start pt-4">
          <input type="checkbox"></input>
          <p className="text-zinc-700 font-arMedium text-xl mobile:text-base">
            {t("paymentForm.component.saveCardInfo")}
          </p>
        </div>
      </form>
    </div>
  );
}

export default PaymentFormComponent;
